import { memo, useCallback, useRef } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";

import s from "./RetryPaymentWStripeFormWrapper.module.scss";
import useRetryPaymentWStripeFormStore from "./store";
import useStripePaymentMethodStore from "../../../../../components/WLStripePaymentMethods/store";
import useWLPromoStore from "../../../../../components/WLPromoField/store";
import { usePurchaseV2Store } from "../../store";
import { useWebUserStore } from "../../../../../CommonStores/user/users";
import { useFormsStore } from "../../../../../../Common/store/common";
import {
  getErrorMsg,
  useEffectOnlyOnce,
} from "../../../../../../Common/utils/common";
import { bugNotif } from "../../../../../api/bugsnag";
import { closeOutline } from "ionicons/icons";
import { WLCOLORS } from "../../../../../../Common/config/global";
import RetryPaymentWStripeFormElementsProvider from "./RetryPaymentWStripeFormElementsProvider/RetryPaymentWStripeFormElementsProvider";
import { isNull } from "lodash";
import { retryPaymentWithStripeForm } from "../../../../../api/stripe";
import { resolveOfferViewedAtFromSearchParams } from "../../../../../utils/resolveOfferViewedAtFromSearchParams";

const RetryPaymentWStripeFormWrapper = () => {
  const modalRef = useRef<HTMLIonModalElement>(null);

  const { user } = useWebUserStore();

  const { setFormLoading } = useFormsStore();
  const { setClientSecretAndInvoiceToPay, clientSecretAndInvoiceToPay, reset } =
    useRetryPaymentWStripeFormStore();
  const { selectedProduct } = usePurchaseV2Store();

  const createRetryPaymentInvoiceForStripeForm = useCallback(async () => {
    const { selectedProduct, setError } = usePurchaseV2Store.getState();

    const existingSubscription =
      useStripePaymentMethodStore.getState().existingSubscription;

    try {
      setFormLoading(true);
      const { user } = useWebUserStore.getState();
      const { validPromoCode, validStripeCoupon } = useWLPromoStore.getState();
      const searchParams = new URLSearchParams(window.location.search);
      const offerViewedAt =
        resolveOfferViewedAtFromSearchParams(searchParams);

      if (user === undefined || isNull(user) || user.stripeId === undefined) {
        throw new Error("No user found. Please login");
      }

      if (!existingSubscription) {
        return setError("No existing subscription found. Please re-open app.");
      }

      if (
        selectedProduct === undefined ||
        isNull(selectedProduct) ||
        selectedProduct.stripePriceId === undefined
      ) {
        throw new Error("Invalid Subscription. Please Re-load the app.");
      }

      const stripeId = user.stripeId;
      const createdPaymentWithStripeForm = await retryPaymentWithStripeForm({
        customerId: stripeId,
        subscriptionId: existingSubscription.id,
        couponId: validStripeCoupon?.id || "",
        promoCode: validPromoCode || "",
        offerViewedAt,
      });

      const clientSecret = createdPaymentWithStripeForm.data.clientSecret;

      setFormLoading(false);
      setClientSecretAndInvoiceToPay({ clientSecret });
    } catch (error) {
      setFormLoading(false);
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg);
      bugNotif("createRetryPaymentInvoiceForStripeForm", rawErrorMsg);
    }
  }, []);

  const dismiss = () => {
    modalRef.current?.dismiss();
    reset();
  };

  useEffectOnlyOnce(() => {
    if (!!user && !!user.stripeId && !!selectedProduct) {
      const { setUseOtherPaymentMethodFunc } =
        useStripePaymentMethodStore.getState();
      setUseOtherPaymentMethodFunc(createRetryPaymentInvoiceForStripeForm);
    }

    return () => {
      console.log("RetryPaymentWStripeFormWrapper - will reset stripe form");
      reset();
    };
  });

  return (
    <IonModal
      className={s.modal}
      onDidDismiss={dismiss}
      isOpen={!!clientSecretAndInvoiceToPay}
    >
      <IonHeader className={s.header}>
        <IonToolbar className={s.toolbar}>
          <IonTitle className="wl-body-3-gilroy dark ion-no-padding ion-text-center">
            Use other payment method
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={dismiss} color={WLCOLORS.dark} fill="clear">
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className={s.content} fullscreen>
        {!!clientSecretAndInvoiceToPay && (
          <RetryPaymentWStripeFormElementsProvider
            clientSecret={clientSecretAndInvoiceToPay.clientSecret}
          />
        )}
      </IonContent>
    </IonModal>
  );
};

export default memo(RetryPaymentWStripeFormWrapper);
